// src/components/common/IndustrySelect.js
// Reusable industry selection dropdown with organization-aware taxonomy

import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  FormHelperText,
  CircularProgress,
  Chip
} from '@mui/material';
import { useIndustryTaxonomy, getCanonicalFromDisplay } from '../../utils/taxonomyUtils';
import { db } from '../../firebase';

const IndustrySelect = ({
  value = '',
  onChange,
  organizationId,
  required = false,
  disabled = false,
  error = '',
  helperText = '',
  size = 'medium',
  showChip = false,
  label = 'Industry',
  fullWidth = true,
  fieldName = 'industryCanonical',  // Target field for saves (default: canonical)
  allowOther: allowOtherProp        // Allow override from parent, otherwise use org setting
}) => {
  const { taxonomy, rawTaxonomy, loading, allowOther: orgAllowOther } = useIndustryTaxonomy(db, organizationId);
  
  // Allow parent to override org's allowOther setting (for special cases)
  const allowOther = allowOtherProp !== undefined ? allowOtherProp : orgAllowOther;
  
  // Local state for the custom input field
  const [customInputValue, setCustomInputValue] = useState('');

  // Derive whether to show custom input from the value prop
  // Show if: value is "Other" OR value is non-empty and not in taxonomy
  const isCustomValue = value && value !== 'Other' && !taxonomy.includes(value);
  const showCustomInput = value === 'Other' || isCustomValue;
  
  // What to display in the Select dropdown
  const selectDisplayValue = showCustomInput ? 'Other' : (value || '');

  // Sync custom input field when value changes externally
  useEffect(() => {
    if (isCustomValue) {
      setCustomInputValue(value);
    } else if (value !== 'Other') {
      setCustomInputValue('');
    }
  }, [value, isCustomValue]);

  // Handle dropdown selection
  const handleSelectChange = (event) => {
    const selectedValue = event.target.value;
    
    if (selectedValue === 'Other') {
      setCustomInputValue('');
      onChange('Other'); // Set to "Other" so showCustomInput becomes true
    } else {
      setCustomInputValue('');
      // Map display label to canonical value if org uses object taxonomy
      const canonicalValue = rawTaxonomy 
        ? getCanonicalFromDisplay(selectedValue, rawTaxonomy)
        : selectedValue;
      onChange(canonicalValue);
    }
  };

  // Handle custom input changes
  // Custom values bypass canonical mapping - they go to Savvai review queue as-is
  const handleCustomInputChange = (event) => {
    const newValue = event.target.value;
    setCustomInputValue(newValue);
    // Update parent with trimmed value, or keep "Other" if empty
    onChange(newValue.trim() || 'Other');
  };

  // Chip display mode
  if (showChip) {
    if (!value || value === 'Other') {
      return (
        <Chip 
          label="Not specified" 
          size="small" 
          variant="outlined" 
          sx={{ color: 'text.secondary' }}
        />
      );
    }
    return (
      <Chip 
        label={value} 
        size="small" 
        variant="outlined"
        color="primary"
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} size={size}>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} disabled>
          <MenuItem value="">Loading...</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <Box>
      <FormControl 
        fullWidth={fullWidth} 
        size={size} 
        required={required}
        error={!!error}
        disabled={disabled}
      >
        <InputLabel id="industry-select-label">{label}</InputLabel>
        <Select
          labelId="industry-select-label"
          id="industry-select"
          value={selectDisplayValue}
          label={label}
          onChange={handleSelectChange}
        >
          <MenuItem value="">
            <em>Select {label.toLowerCase()}...</em>
          </MenuItem>
          
          {taxonomy.filter(ind => ind !== 'Other').map((industry) => (
            <MenuItem key={industry} value={industry}>
              {industry}
            </MenuItem>
          ))}
          
          {allowOther && (
            <MenuItem value="Other">
              <em>Other (specify below)</em>
            </MenuItem>
          )}
        </Select>
        
        {helperText && !error && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
        {error && (
          <FormHelperText error>{error}</FormHelperText>
        )}
      </FormControl>

      {showCustomInput && allowOther && (
        <TextField
          fullWidth={fullWidth}
          size={size}
          label="Specify Industry"
          placeholder="Enter custom industry..."
          value={customInputValue}
          onChange={handleCustomInputChange}
          disabled={disabled}
          sx={{ mt: 2 }}
          autoFocus
          helperText="Enter the specific industry for this client"
        />
      )}
    </Box>
  );
};

/**
 * Simplified version for filter dropdowns (includes "All" option)
 */
export const IndustryFilterSelect = ({
  value = 'ALL',
  onChange,
  organizationId,
  size = 'small',
  fullWidth = true,
  includeAll = true,
  label = 'Industry',
  fieldName = 'industryCanonical'  // Field to query against
}) => {
  const { taxonomy, loading } = useIndustryTaxonomy(db, organizationId);

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} size={size}>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} disabled>
          <MenuItem value="">Loading...</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth={fullWidth} size={size}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        {includeAll && (
          <MenuItem value="ALL">All Industries</MenuItem>
        )}
        {taxonomy.filter(t => t !== 'Other').map((industry) => (
          <MenuItem key={industry} value={industry}>
            {industry}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default IndustrySelect;